/**
 * Standalone full-edit dialog for a single product.
 * - Edits ALL key fields (name, sku, brand, prices, mayoreo, stock, comisión, imágenes, promo).
 * - Funciona para producto raíz Y para una variante (parent_product_id presente).
 * - Soporta MODO CREAR (no productId) y MODO EDITAR (productId).
 * - Admin: aplica directo. Clerk: pide aprobación por Telegram (product-create / product-change / promo-change).
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   product: object | null    (objeto producto a editar; null = crear)
 *   parentProduct?: object    (si vas a crear o editar una variante, pasa el padre para heredar contexto)
 *   onSaved?: () => void      (callback tras guardar/aprobar)
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Image as ImageIcon, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { useAuth } from '@/contexts/AuthContext';
import { compressImage } from '@/lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '', sku: '', brand: '', category_id: 'none',
  precio_1: '', precio_2: '', precio_3: '', precio_4: '', precio_5: '',
  mayoreo: '', medio_mayoreo: '',
  stock: '0', min_stock: '10', default_commission: '0',
  image_url: '', extra_images: [],
  variant_name: '',
  has_promo: false, promo_type: 'flat', promo_price: '', promo_min_qty: '', promo_end_date: '',
  promo_local_only: false,
  visible_in_pos: true,
  visible_in_catalog: true,
  is_new_arrival: false,
  product_info: ''
};

export const ProductEditDialog = ({ open, onClose, product, parentProduct, onSaved }) => {
  const { categories, refreshProducts } = useDataCache();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  // IDs de aprobaciones pendientes para polling (sobrevive al cierre del dialog).
  // Usamos un ref interno para no reiniciar el setInterval cada vez que el array cambia.
  const [pendingApprovalIds, setPendingApprovalIds] = useState([]);
  const pendingApprovalIdsRef = useRef([]);
  useEffect(() => { pendingApprovalIdsRef.current = pendingApprovalIds; }, [pendingApprovalIds]);

  // Contador que se incrementa después de cada upload para forzar re-montado
  // de los <input type="file">. Necesario en Android/Chrome donde el mismo
  // control retiene URI cacheado del media provider entre capturas.
  const [imgInputKey, setImgInputKey] = useState(0);
  // Últimos fingerprints de fotos ya procesadas en esta sesión de edición.
  const seenFilesRef = useRef(new Set());

  const isVariant = !!product?.parent_product_id || !!parentProduct;

  useEffect(() => {
    if (!open) return;
    if (product) {
      // Fetch en background el doc completo (con extra_images) para no depender
      // de que el listado del POS las traiga. Si falla, se usa lo que ya teníamos.
      (async () => {
        try {
          const { data: full } = await axios.get(`${API}/products/full/${product.id}`, { withCredentials: true });
          setForm(prev => ({
            ...prev,
            extra_images: full.extra_images || prev.extra_images || [],
          }));
        } catch { /* mantenemos extras vacíos si falla */ }
      })();
      setForm({
        name: product.name || '',
        sku: product.sku || '',
        brand: product.brand || '',
        category_id: product.category_id || 'none',
        precio_1: product.precio_1 ?? '',
        precio_2: product.precio_2 ?? '',
        precio_3: product.precio_3 ?? '',
        precio_4: product.precio_4 ?? '',
        precio_5: product.precio_5 ?? '',
        mayoreo: product.mayoreo ?? '',
        medio_mayoreo: product.medio_mayoreo ?? '',
        stock: String(product.stock ?? 0),
        min_stock: String(product.min_stock ?? 10),
        default_commission: String(product.default_commission ?? 0),
        image_url: product.image_url || '',
        extra_images: product.extra_images || [],
        variant_name: product.variant_name || '',
        has_promo: !!product.has_promo,
        promo_type: product.promo_type || 'flat',
        promo_price: product.promo_price ?? '',
        promo_min_qty: product.promo_min_qty ?? '',
        promo_end_date: product.promo_end_date || '',
        promo_local_only: !!product.promo_local_only,
        visible_in_pos: product.visible_in_pos !== false,  // default true
        visible_in_catalog: product.visible_in_catalog !== false,  // default true
        is_new_arrival: !!product.is_new_arrival,
        product_info: product.product_info || ''
      });
    } else {
      // create mode
      setForm({
        ...emptyForm,
        // si crea variante, hereda nombre del padre
        name: parentProduct ? parentProduct.name : '',
        brand: parentProduct?.brand || '',
        category_id: parentProduct?.category_id || 'none'
      });
    }
  }, [open, product, parentProduct]);

  // Reset del fingerprint de archivos cuando se abre un producto distinto,
  // para no bloquear que reutilices la misma foto en otro producto.
  useEffect(() => {
    if (seenFilesRef.current) seenFilesRef.current.clear();
  }, [open, product?.id, parentProduct?.id]);

  // Polling de aprobaciones pendientes (clerks): sobrevive al cierre del dialog.
  // El intervalo se crea UNA SOLA VEZ; lee los IDs vía ref para no reiniciarse.
  useEffect(() => {
    const interval = setInterval(async () => {
      const ids = pendingApprovalIdsRef.current;
      if (!ids || ids.length === 0) return;
      const remaining = [];
      let resolvedAny = false;
      for (const id of ids) {
        try {
          const { data } = await axios.get(`${API}/approvals/${id}`, { withCredentials: true });
          if (data.status === 'approved') {
            toast.success('✅ Aprobado: cambios aplicados al producto');
            resolvedAny = true;
          } else if (data.status === 'rejected') {
            toast.error('❌ Rechazado por el admin');
            resolvedAny = true;
          } else {
            remaining.push(id);
          }
        } catch {
          remaining.push(id);
        }
      }
      if (resolvedAny) {
        refreshProducts();
        onSaved?.();
        setPendingApprovalIds(remaining);
      }
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  const setF = (patch) => setForm(f => ({ ...f, ...patch }));

  const _fingerprintFile = (f) => `${f.name}::${f.size}::${f.lastModified}`;

  const onImage = async (e, multi = false) => {
    const filesRaw = Array.from(e.target.files || []);
    if (filesRaw.length === 0) return;
    try {
      const filesToProcess = [];
      for (const f of filesRaw) {
        const fp = _fingerprintFile(f);
        if (seenFilesRef.current.has(fp)) {
          toast.warning('⚠️ Android devolvió la misma foto anterior. Toma la foto de nuevo desde la cámara.');
          continue;
        }
        seenFilesRef.current.add(fp);
        // Envolver en Blob fresco rompe cualquier URI cacheado por el sistema.
        const buf = await f.arrayBuffer();
        const freshBlob = new Blob([buf], { type: f.type || 'image/jpeg' });
        // File es sub-clase de Blob; construimos un File nuevo para preservar el name
        const freshFile = new File([freshBlob], f.name || 'foto.jpg', {
          type: f.type || 'image/jpeg',
          lastModified: f.lastModified || Date.now(),
        });
        filesToProcess.push(freshFile);
      }
      if (multi) {
        for (const f of filesToProcess) {
          const du = await compressImage(f);
          setForm(prev => ({ ...prev, extra_images: [...(prev.extra_images || []), du] }));
        }
      } else if (filesToProcess.length > 0) {
        const du = await compressImage(filesToProcess[0]);
        setF({ image_url: du });
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      e.target.value = '';
      // Fuerza que la próxima captura remonte inputs limpios (arregla Android)
      setImgInputKey(k => k + 1);
    }
  };

  const removeExtra = (idx) => setF({ extra_images: form.extra_images.filter((_, i) => i !== idx) });

  const buildProductData = () => {
    const num = (v) => parseFloat(v || 0) || 0;
    const int = (v) => parseInt(v) || 0;
    return {
      name: form.name.trim(),
      sku: form.sku.trim(),
      brand: form.brand || null,
      category_id: form.category_id === 'none' ? null : form.category_id,
      precio_1: num(form.precio_1),
      precio_2: num(form.precio_2),
      precio_3: num(form.precio_3),
      precio_4: num(form.precio_4),
      precio_5: num(form.precio_5),
      mayoreo: num(form.mayoreo),
      medio_mayoreo: num(form.medio_mayoreo),
      stock: int(form.stock),
      min_stock: int(form.min_stock),
      default_commission: num(form.default_commission),
      image_url: form.image_url || null,
      extra_images: form.extra_images || [],
      variant_name: isVariant ? (form.variant_name || null) : null,
      parent_product_id: isVariant ? (product?.parent_product_id || parentProduct?.id || null) : null,
      has_variants: false,
      has_promo: !!form.has_promo,
      promo_type: form.has_promo ? form.promo_type : null,
      promo_price: form.has_promo && form.promo_price !== '' ? parseFloat(form.promo_price) : null,
      promo_min_qty: form.has_promo && form.promo_type === 'min_qty' && form.promo_min_qty !== '' ? parseInt(form.promo_min_qty) : null,
      promo_end_date: form.has_promo && form.promo_end_date ? form.promo_end_date : null,
      promo_local_only: !!form.has_promo && !!form.promo_local_only,
      visible_in_pos: form.visible_in_pos !== false,
      visible_in_catalog: form.visible_in_catalog !== false,
      is_new_arrival: !!form.is_new_arrival,
      product_info: (form.product_info || '').trim() || null
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sku.trim()) {
      toast.error('Nombre y SKU son obligatorios');
      return;
    }
    if (isVariant && !form.variant_name.trim()) {
      toast.error('Esta variante necesita un nombre (ej: "Roja", "80x80")');
      return;
    }
    setSaving(true);
    try {
      const data = buildProductData();
      if (product) {
        // EDITAR existente: NO enviar has_variants ni parent_product_id —
        // el backend conservará esos campos del documento existente.
        delete data.has_variants;
        if (!isVariant) delete data.parent_product_id;
        if (isAdmin) {
          await axios.put(`${API}/products/${product.id}`, data, { withCredentials: true });
          toast.success('Producto actualizado');
        } else {
          const { data: resp } = await axios.post(
            `${API}/approvals/product-change`,
            { product_id: product.id, update_data: data },
            { withCredentials: true }
          );
          if (resp.status === 'approved') {
            toast.success('Cambios aplicados');
          } else {
            toast.info('⏳ Cambios enviados al admin para aprobación');
            if (resp.id) setPendingApprovalIds(prev => [...prev, resp.id]);
          }
        }
      } else {
        // CREAR nuevo
        if (isAdmin) {
          await axios.post(`${API}/products`, data, { withCredentials: true });
          toast.success(isVariant ? 'Variante creada' : 'Producto creado');
        } else {
          const { data: resp } = await axios.post(
            `${API}/approvals/product-create`,
            { create_data: data },
            { withCredentials: true }
          );
          if (resp.status === 'approved') {
            toast.success('Creado');
          } else {
            toast.info('⏳ Solicitud enviada al admin para aprobación');
            if (resp.id) setPendingApprovalIds(prev => [...prev, resp.id]);
          }
        }
      }
      onClose();
      refreshProducts();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            {product
              ? (isVariant ? `Editar variante: ${product.variant_name || ''}` : `Editar producto: ${product.name}`)
              : (isVariant ? 'Nueva variante' : 'Nuevo producto')}
            {!isAdmin && <span className="ml-3 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">Cajero · requiere aprobación</span>}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Tabs defaultValue="general">
            <TabsList className="grid grid-cols-4">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="precios">Precios</TabsTrigger>
              <TabsTrigger value="fotos">Fotos</TabsTrigger>
              <TabsTrigger value="oferta">Oferta</TabsTrigger>
            </TabsList>

            {/* GENERAL */}
            <TabsContent value="general" className="pt-4 space-y-4">
              {isVariant && (
                <div>
                  <Label>Nombre de la variante *</Label>
                  <Input value={form.variant_name} onChange={e => setF({ variant_name: e.target.value })} placeholder="ej: Blanca, 80x80, M..." data-testid="ped-variant-name" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del producto *</Label>
                  <Input value={form.name} onChange={e => setF({ name: e.target.value })} required data-testid="ped-name" />
                </div>
                <div>
                  <Label>SKU *</Label>
                  <Input value={form.sku} onChange={e => setF({ sku: e.target.value })} required data-testid="ped-sku" />
                </div>
                <div>
                  <Label>Marca</Label>
                  <Input value={form.brand} onChange={e => setF({ brand: e.target.value })} />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={form.category_id} onValueChange={v => setF({ category_id: v })}>
                    <SelectTrigger data-testid="ped-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin categoría</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input type="number" value={form.stock} onChange={e => setF({ stock: e.target.value })} data-testid="ped-stock" />
                </div>
                <div>
                  <Label>Stock mínimo</Label>
                  <Input type="number" value={form.min_stock} onChange={e => setF({ min_stock: e.target.value })} />
                </div>
              </div>

              {/* Visibilidad en POS */}
              <div className="bg-slate-50 border border-border rounded p-3 flex items-center justify-between">
                <div>
                  <Label className="font-semibold">Disponible en Punto de Venta</Label>
                  <p className="text-xs text-slate-500 mt-1">Si lo apagas, este producto NO aparecerá en el POS (útil para productos descontinuados o no listos para venta).</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.visible_in_pos}
                  onClick={() => setF({ visible_in_pos: !form.visible_in_pos })}
                  data-testid="ped-visible-in-pos"
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent btn-transition ${
                    form.visible_in_pos ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 btn-transition ${
                    form.visible_in_pos ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Visibilidad en Catálogo público / Ofertas */}
              <div className="bg-slate-50 border border-border rounded p-3 flex items-center justify-between">
                <div>
                  <Label className="font-semibold">Visible en Catálogo y Ofertas</Label>
                  <p className="text-xs text-slate-500 mt-1">Si lo apagas, este producto NO aparecerá en las páginas públicas de <b>catálogo</b> ni de <b>ofertas</b>. Sigue disponible en el POS (a menos que también apagues el switch de arriba).</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.visible_in_catalog !== false}
                  onClick={() => setF({ visible_in_catalog: form.visible_in_catalog === false })}
                  data-testid="ped-visible-in-catalog"
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent btn-transition ${
                    form.visible_in_catalog !== false ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 btn-transition ${
                    form.visible_in_catalog !== false ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* PRODUCTO NUEVO */}
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between">
                <div className="flex items-start gap-2">
                  <span className="text-lg" role="img" aria-label="nuevo">🆕</span>
                  <div>
                    <Label className="font-semibold text-emerald-800">Marcar como PRODUCTO NUEVO</Label>
                    <p className="text-xs text-emerald-700/80 mt-0.5">
                      {product ? 'Al guardar reinicia los 10 días con la etiqueta “Nuevo” en catálogo y ofertas.' : 'Aparece con etiqueta “Nuevo” en catálogo y ofertas durante 10 días.'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!form.is_new_arrival}
                  onClick={() => setF({ is_new_arrival: !form.is_new_arrival })}
                  data-testid="ped-is-new-arrival"
                  className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent btn-transition ${
                    form.is_new_arrival ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 btn-transition ${
                    form.is_new_arrival ? 'translate-x-5' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Información del producto */}
              <div>
                <Label className="font-semibold flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold" style={{ fontFamily: 'Georgia, serif' }}>i</span>
                  Información del producto
                </Label>
                <p className="text-xs text-slate-500 mt-0.5 mb-1.5">Aparece al tocar el ícono “i” en catálogo y ofertas.</p>
                <textarea
                  value={form.product_info || ''}
                  onChange={e => setF({ product_info: e.target.value })}
                  placeholder="Medidas, material, uso, garantía…"
                  rows={3}
                  maxLength={800}
                  data-testid="ped-product-info"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 resize-y"
                />
                <div className="text-[10px] text-slate-400 text-right">{(form.product_info || '').length} / 800</div>
              </div>
            </TabsContent>

            {/* PRECIOS */}
            <TabsContent value="precios" className="pt-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                {[1,2,3,4,5].map(n => (
                  <div key={n}>
                    <Label>Precio {n}</Label>
                    <Input type="number" step="0.01" value={form[`precio_${n}`]} onChange={e => setF({ [`precio_${n}`]: e.target.value })} data-testid={`ped-precio-${n}`} />
                  </div>
                ))}
                <div>
                  <Label>Mayoreo</Label>
                  <Input type="number" step="0.01" value={form.mayoreo} onChange={e => setF({ mayoreo: e.target.value })} />
                </div>
                <div>
                  <Label>Medio mayoreo</Label>
                  <Input type="number" step="0.01" value={form.medio_mayoreo} onChange={e => setF({ medio_mayoreo: e.target.value })} />
                </div>
                <div>
                  <Label>Comisión por defecto</Label>
                  <Input type="number" step="0.01" value={form.default_commission} onChange={e => setF({ default_commission: e.target.value })} data-testid="ped-commission" />
                </div>
              </div>
            </TabsContent>

            {/* FOTOS */}
            <TabsContent value="fotos" className="pt-4 space-y-4">
              <div>
                <Label>Imagen principal</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.image_url ? (
                    <div className="relative">
                      <img src={form.image_url} alt="" className="w-20 h-20 object-cover rounded" />
                      <button type="button" onClick={() => setF({ image_url: '' })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-slate-100 rounded flex items-center justify-center">
                      <ImageIcon size={32} className="text-slate-400" />
                    </div>
                  )}
                  <Input key={`main-${imgInputKey}`} type="file" accept="image/*,.heic,.heif" onChange={(e) => onImage(e, false)} data-testid="ped-image" />
                </div>
              </div>
              <div>
                <Label>Galería (extra)</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {(form.extra_images || []).map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                      <button type="button" onClick={() => removeExtra(idx)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-violet-500">
                    <input key={`extra-${imgInputKey}`} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => onImage(e, true)} />
                    <span className="text-2xl text-slate-400">+</span>
                  </label>
                </div>
              </div>
            </TabsContent>

            {/* OFERTA */}
            <TabsContent value="oferta" className="pt-4 space-y-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ped-has-promo" checked={form.has_promo} onChange={e => setF({ has_promo: e.target.checked })} data-testid="ped-has-promo" />
                <Label htmlFor="ped-has-promo">Activar oferta</Label>
              </div>
              {form.has_promo && (
                <div className="space-y-3 pl-6 border-l-2 border-violet-200">
                  <div>
                    <Label>Tipo de oferta</Label>
                    <Select value={form.promo_type} onValueChange={v => setF({ promo_type: v })}>
                      <SelectTrigger data-testid="ped-promo-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Precio fijo de oferta</SelectItem>
                        <SelectItem value="min_qty">Precio especial con cantidad mínima</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Precio de oferta</Label>
                      <Input type="number" step="0.01" value={form.promo_price} onChange={e => setF({ promo_price: e.target.value })} data-testid="ped-promo-price" />
                    </div>
                    {form.promo_type === 'min_qty' && (
                      <div>
                        <Label>Cantidad mínima</Label>
                        <Input type="number" value={form.promo_min_qty} onChange={e => setF({ promo_min_qty: e.target.value })} />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Vigencia (opcional)</Label>
                    <Input type="date" value={form.promo_end_date} onChange={e => setF({ promo_end_date: e.target.value })} />
                  </div>
                  <div className="bg-violet-50 border border-violet-200 rounded p-3">
                    <Label className="text-sm font-semibold mb-2 block">Alcance de la oferta</Label>
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setF({ promo_local_only: false })}
                        data-testid="ped-promo-scope-both"
                        className={`flex-1 py-2 px-3 rounded border text-sm font-medium ${
                          !form.promo_local_only ? 'border-violet-600 bg-violet-600 text-white' : 'border-border bg-white'
                        }`}>
                        🏪 Local y envío
                      </button>
                      <button type="button"
                        onClick={() => setF({ promo_local_only: true })}
                        data-testid="ped-promo-scope-local"
                        className={`flex-1 py-2 px-3 rounded border text-sm font-medium ${
                          form.promo_local_only ? 'border-violet-600 bg-violet-600 text-white' : 'border-border bg-white'
                        }`}>
                        🏬 Solo local
                      </button>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">
                      Si eliges <b>Solo local</b>, el descuento NO se aplicará en pedidos en modo Envío y el bot lo indicará en Telegram.
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="ped-save">
              {saving ? 'Guardando...' : (product ? 'Guardar cambios' : 'Crear')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductEditDialog;
